import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { authLogin, authMe } from "@/lib/portalApi";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem("venka_token"));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const me = await authMe();
        if (active) setUser(me);
      } catch (e) {
        localStorage.removeItem("venka_token");
        if (active) {
          setToken(null);
          setUser(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  /** Returns the user, or { twoFactor: true, challenge } when the account has 2FA (finish with loginWithToken). */
  const login = useCallback(async (email, password) => {
    const data = await authLogin(email, password);
    if (data.two_factor_required) return { twoFactor: true, challenge: data.challenge };
    localStorage.setItem("venka_token", data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("venka_token");
    setToken(null);
    setUser(null);
  }, []);

  const loginWithToken = useCallback((newToken, newUser) => {
    localStorage.setItem("venka_token", newToken);
    setToken(newToken);
    if (newUser) setUser(newUser);
  }, []);

  const refreshMe = useCallback(async () => { try { setUser(await authMe()); } catch (e) { /* ignore */ } }, []);

  const value = {
    token,
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    isStaff: user?.role === "admin" || user?.role === "agent",
    isCustomer: user?.role === "customer",
    login,
    loginWithToken,
    refreshMe,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
